import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';

import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import {
  invokeCatalogListApi,
  resolveCatalogEntityId,
  resolveCatalogEntityLabel,
  CATALOG_ENTITY_REGISTRY,
} from '@/adapters/shared/catalog/catalog-entity-registry';
import Combobox, { type ComboboxCommitDetails } from '@/components/ui/combobox';
import FormCheckbox from '@/components/ui/form-checkbox';
import FormField from '@/components/ui/form-field';
import FormInput from '@/components/ui/form-input';
import FormSelect from '@/components/ui/form-select';
import FormTextarea from '@/components/ui/form-textarea';
import MultiSelect from '@/components/ui/multi-select';
import { SelectGroup, SelectItem, SelectLabel } from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import ArraySchemaField from '@/components/tenants/secrets/array-schema-field';
import KeyValueField from '@/components/tenants/secrets/key-value-field';
import type {
  SchemaProperty,
  FormSchema,
  XLookup,
  XLookupSource,
} from '@/types/shared/form-schema';
import type { JSONObject, JSONValue } from '@/types/shared/json-schema';
import type { UUID } from '@/utils/uuid';
import { isFieldEnabled } from '@/utils/json-schema-adapter';
import { getIconPixels } from '@/theme/size-tokens';
import { AlertCircle, Info } from 'lucide-react';

// ─── SchemaFormFields ─────────────────────────────────────────────────────────

// ─── Catalog Lookup ───────────────────────────────────────────────────────────

type LookupOption = {
  label: string;
  value: string;
  description?: string;
  /** Catalog `entity-type` this option was resolved from (see ADR-015). */
  sourceType: string;
};

/** A set of options that all originate from one catalog source, for grouped rendering. */
type LookupOptionGroup = {
  sourceType: string;
  label: string;
  options: LookupOption[];
};

/**
 * Combined result of resolving every source of a (possibly multi-source)
 * x-lookup. `options` is the flat, deduped, label-sorted union used for value
 * resolution and flat controls; `groups` preserves source grouping for controls
 * that render option groups. `emptySources`/`erroredSources` drive the ADR-015
 * fallback: disable only when every source is empty or errored.
 */
type CatalogLookupResult = {
  options: LookupOption[];
  groups: LookupOptionGroup[];
  isPending: boolean;
  emptySources: string[];
  erroredSources: string[];
};

type CatalogLookupConfig = {
  tenantId: string;
  connectorId: string;
  xLookup: XLookup;
};

/** Resolve a dot-notation path (e.g. "primary_email.address") against an object. */
function resolveKeyPath(obj: Record<string, unknown>, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (cur, key) =>
        cur != null && typeof cur === 'object'
          ? (cur as Record<string, unknown>)[key]
          : undefined,
      obj
    );
}

/** Paginates one catalog source and maps its objects to lookup options (ADR-007). */
async function fetchCatalogSourceOptions(
  tenantId: string,
  connectorId: string,
  source: XLookupSource
): Promise<LookupOption[]> {
  const entityId = resolveCatalogEntityId(source.entityType);
  const apiMethod = entityId
    ? CATALOG_ENTITY_REGISTRY[entityId].apiMethod
    : undefined;
  if (!apiMethod) {
    throw new Error(`Unknown catalog entity type: ${source.entityType}`);
  }

  const options: LookupOption[] = [];
  let cursor: string | undefined;

  do {
    const res = await invokeCatalogListApi(
      apiMethod,
      tenantId as UUID,
      connectorId as UUID,
      { limit: 1000, next: cursor }
    );
    if (!res?.data) {
      throw new Error('Catalog lookup failed');
    }
    const page = res.data as {
      models?: Record<string, unknown>[];
      next?: string;
    };
    for (const m of page.models ?? []) {
      const label = String(resolveKeyPath(m, source.displayKey) ?? '');
      const value = String(resolveKeyPath(m, source.submitKey) ?? '');
      if (label && value) {
        options.push({
          label,
          value,
          sourceType: source.entityType,
          description: label === value ? undefined : value,
        });
      }
    }
    cursor = page.next || undefined;
  } while (cursor);

  return options;
}

/**
 * Unions the per-source query results into the shape the renderer consumes:
 * dedupe by submitted `value` (first source wins), sort by label, and group by
 * source in configuration order. A source that resolved with zero rows is
 * `empty`; a source whose query failed (including an unknown entity type) is
 * `errored`.
 */
function combineCatalogLookupResults(
  sources: readonly XLookupSource[],
  results: readonly UseQueryResult<LookupOption[]>[]
): CatalogLookupResult {
  const emptySources: string[] = [];
  const erroredSources: string[] = [];
  const seen = new Set<string>();
  const deduped: LookupOption[] = [];

  results.forEach((res, index) => {
    const source = sources[index];
    if (!source) return;
    if (res.isError) {
      erroredSources.push(source.entityType);
      return;
    }
    if (!res.isSuccess) return;

    const options = res.data ?? [];
    if (options.length === 0) {
      emptySources.push(source.entityType);
    }
    for (const option of options) {
      if (seen.has(option.value)) continue;
      seen.add(option.value);
      deduped.push(option);
    }
  });

  const options = [...deduped].sort((a, b) => a.label.localeCompare(b.label));

  const groups = sources.flatMap((source) => {
    const groupOptions = options.filter(
      (option) => option.sourceType === source.entityType
    );
    return groupOptions.length > 0
      ? [
          {
            sourceType: source.entityType,
            label: resolveCatalogEntityLabel(source.entityType),
            options: groupOptions,
          },
        ]
      : [];
  });

  const isPending = results.some((res) => !res.isSuccess && !res.isError);

  return { options, groups, isPending, emptySources, erroredSources };
}

/** Shared empty array so a lookup-less render keeps a stable `sources` reference. */
const EMPTY_LOOKUP_SOURCES: readonly XLookupSource[] = [];

/**
 * Resolves every source of an x-lookup (single- or multi-source) and unions the
 * results (ADR-015). Each source is an independent, cached catalog query keyed
 * identically to the ADR-007 single-source key, so existing lookups reuse their
 * cache entries.
 */
function useCatalogLookup(
  config: CatalogLookupConfig | undefined
): CatalogLookupResult {
  const sources = config?.xLookup.sources ?? EMPTY_LOOKUP_SOURCES;

  const combine = useCallback(
    (results: readonly UseQueryResult<LookupOption[]>[]) =>
      combineCatalogLookupResults(sources, results),
    [sources]
  );

  return useQueries({
    queries: sources.map((source) => ({
      queryKey: [
        'catalog-lookup',
        config?.tenantId,
        config?.connectorId,
        source.entityType,
        source.displayKey,
        source.submitKey,
      ],
      queryFn: () =>
        fetchCatalogSourceOptions(
          config!.tenantId,
          config!.connectorId,
          source
        ),
      enabled: !!config,
      staleTime: 1000 * 60 * 5,
    })),
    combine,
  });
}

// ─── Lookup Field Renderers ───────────────────────────────────────────────────

type LookupFieldProps = {
  fieldId: string;
  formInputType: string;
  options: LookupOption[];
  groups: LookupOptionGroup[];
  label: string;
  labelSuffix?: ReactNode;
  error?: string;
  value: SchemaFieldValue;
  onChange: (value: SchemaFieldValue) => void;
  required: boolean;
  disabled?: boolean;
  inputType: string;
  placeholder: string;
};

function findLookupOptionByValue(
  options: LookupOption[],
  value: string
): LookupOption | undefined {
  return options.find((option) => option.value === value);
}

function resolveLookupDisplayValue(
  submittedValue: string,
  options: LookupOption[]
): string {
  return (
    findLookupOptionByValue(options, submittedValue)?.label ?? submittedValue
  );
}

function lookupDisplayMatchesValue(
  displayValue: string,
  submittedValue: string,
  options: LookupOption[]
): boolean {
  const matchedOption = findLookupOptionByValue(options, submittedValue);
  if (matchedOption) {
    return matchedOption.label === displayValue;
  }
  return displayValue === submittedValue;
}

function LookupComboboxField({
  fieldId,
  options,
  groups,
  label,
  labelSuffix,
  error,
  value,
  onChange,
  required,
  disabled,
  inputType,
  placeholder,
}: Readonly<Omit<LookupFieldProps, 'formInputType'>>) {
  const submittedValue = typeof value === 'string' ? value : '';
  const [draftValue, setDraftValue] = useState(() =>
    resolveLookupDisplayValue(submittedValue, options)
  );

  useEffect(() => {
    setDraftValue((prev) => {
      if (lookupDisplayMatchesValue(prev, submittedValue, options)) return prev;
      return resolveLookupDisplayValue(submittedValue, options);
    });
  }, [options, submittedValue]);

  const handleValueChange = (nextValue: string) => {
    setDraftValue(nextValue);
  };

  const handleCommit = (nextValue: string, details?: ComboboxCommitDetails) => {
    if (details?.source === 'option') {
      const selectedOption = findLookupOptionByValue(options, nextValue);
      if (selectedOption) {
        setDraftValue(selectedOption.label);
        onChange(selectedOption.value);
        return;
      }
    }

    setDraftValue(nextValue);
    onChange(nextValue);
  };

  return (
    <FormField
      id={fieldId}
      label={label}
      labelSuffix={labelSuffix}
      error={error}
      required={required}
      disabled={disabled}
    >
      <Combobox
        options={options}
        groups={groups}
        value={draftValue}
        onValueChange={handleValueChange}
        onCommit={handleCommit}
        placeholder={`Select or ${placeholder.toLowerCase()}`}
        disabled={disabled}
        allowFreeText
        sanitize={(raw) => raw}
        inputType={inputType}
      />
    </FormField>
  );
}

/**
 * Resolves the catalog-backed form control for a given `form-input-type`.
 * Returns `null` when the input type is unrecognised so the caller can
 * fall back to the default schema-driven control (ADR-007 §4).
 */
/** Renders the option children of a lookup select, grouped by source when more than one source resolved. */
function renderLookupSelectItems(
  options: LookupOption[],
  groups: LookupOptionGroup[]
): ReactNode {
  if (groups.length > 1) {
    return groups.map((group) => (
      <SelectGroup key={group.sourceType}>
        <SelectLabel>{group.label}</SelectLabel>
        {group.options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectGroup>
    ));
  }

  return options.map((opt) => (
    <SelectItem key={opt.value} value={opt.value}>
      {opt.label}
    </SelectItem>
  ));
}

function renderLookupField({
  fieldId,
  formInputType,
  options,
  groups,
  label,
  labelSuffix,
  error,
  value,
  onChange,
  required,
  disabled,
  inputType,
  placeholder,
}: LookupFieldProps): ReactNode | null {
  switch (formInputType) {
    case 'multi-select': {
      const selected =
        typeof value === 'string' && value
          ? value
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      return (
        <div className="flex flex-col gap-1">
          <label className="text-foreground text-sm font-medium">
            {label}
            {required && (
              <span className="text-error ml-0.5" aria-hidden="true">
                *
              </span>
            )}
            {labelSuffix && (
              <span className="ml-1.5 inline-flex align-middle">
                {labelSuffix}
              </span>
            )}
          </label>
          <MultiSelect
            options={options}
            value={selected}
            onChange={(vals) => onChange(vals.join('\n'))}
            placeholder={`Select ${label.toLowerCase()}...`}
            disabled={disabled}
          />
        </div>
      );
    }

    case 'datalist': {
      return (
        <LookupComboboxField
          fieldId={fieldId}
          options={options}
          groups={groups}
          label={label}
          labelSuffix={labelSuffix}
          error={error}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          inputType={inputType}
          placeholder={placeholder}
        />
      );
    }

    case 'select':
      return (
        <FormSelect
          label={label}
          labelSuffix={labelSuffix}
          error={error}
          selectLabel={`Select ${label.toLowerCase()}`}
          value={(value as string) || ''}
          onValueChange={(v) => onChange(v)}
          required={required}
          disabled={disabled}
        >
          {renderLookupSelectItems(options, groups)}
        </FormSelect>
      );

    default:
      return null;
  }
}

/** Joins entity types into a readable, comma-separated list. */
function formatSourceList(entityTypes: string[]): string {
  return entityTypes.map((type) => resolveCatalogEntityLabel(type)).join(', ');
}

/**
 * Renders a warning tooltip for x-lookup sources that could not contribute
 * options (ADR-015 §4). Empty sources prompt the operator to run the relevant
 * collector; errored sources note a transient failure. A pure transport failure
 * (no empty sources) is styled as an error so manual entry stays discoverable.
 */
function LookupSourceWarning({
  emptySources,
  erroredSources,
}: Readonly<{ emptySources: string[]; erroredSources: string[] }>) {
  const errorOnly = erroredSources.length > 0 && emptySources.length === 0;
  const triggerClass = errorOnly
    ? 'text-error hover:text-error/80'
    : 'text-warning hover:text-warning/80';
  const ariaLabel = errorOnly
    ? 'Catalog lookup failed'
    : 'Catalog lookup unavailable';

  return (
    <Popover>
      <PopoverTrigger
        type="button"
        className={`${triggerClass} inline-flex shrink-0 items-center transition-colors`}
        aria-label={ariaLabel}
      >
        <AlertCircle size={getIconPixels('xs')} />
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="max-w-sm p-3">
        <div className="flex flex-col gap-2 text-xs">
          {emptySources.length > 0 && (
            <p>
              Selection options for this field are populated from the catalog.
              Run a collector on this connector that populates the{' '}
              <strong>{formatSourceList(emptySources)}</strong> catalog
              collection to enable them.
            </p>
          )}
          {erroredSources.length > 0 && (
            <p>
              Failed to load catalog options from{' '}
              <strong>{formatSourceList(erroredSources)}</strong>. You can enter
              a value manually or try again later.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Value Helpers ────────────────────────────────────────────────────────────

export type SchemaFieldValue = string | JSONObject;

function isRecordValue(value: unknown): value is JSONObject {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function parseObjectValue(value: string): JSONObject | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  try {
    const parsedValue = JSON.parse(trimmedValue);
    if (
      parsedValue != null &&
      typeof parsedValue === 'object' &&
      !Array.isArray(parsedValue)
    ) {
      return parsedValue as JSONObject;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function normalizeNestedFieldValue(
  value: JSONValue | undefined
): SchemaFieldValue | undefined {
  if (value === undefined) return undefined;

  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as JSONObject;
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return value == null ? '' : String(value);
}

function serializePrimitiveValue(
  type: string,
  value: string
): JSONValue | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  if (type === 'boolean') {
    return trimmedValue === 'true';
  }

  if (type === 'number' || type === 'integer') {
    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return value;
}

export function serializeSchemaFieldValue(
  fieldSchema: SchemaProperty,
  value: SchemaFieldValue | undefined
): JSONValue | undefined {
  if (fieldSchema.type === 'array') {
    if (typeof value !== 'string') return undefined;
    const items = value
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    return items.length > 0 ? items : undefined;
  }

  if (fieldSchema.type === 'object') {
    const subProperties = fieldSchema.properties;

    if (subProperties && Object.keys(subProperties).length > 0) {
      const objectValue = isRecordValue(value) ? value : {};
      const entries = Object.entries(subProperties).flatMap(
        ([subKey, subSchema]) => {
          const serializedValue = serializeSchemaFieldValue(
            subSchema,
            normalizeNestedFieldValue(objectValue[subKey])
          );

          return serializedValue === undefined
            ? []
            : [[subKey, serializedValue] as const];
        }
      );

      return entries.length > 0
        ? (Object.fromEntries(entries) as JSONObject)
        : undefined;
    }

    // Object without sub-properties — value is a typed object from KeyValueField.
    if (isRecordValue(value)) {
      const entries = Object.entries(value).filter(([k]) => k.trim() !== '');
      return entries.length > 0
        ? (Object.fromEntries(entries) as JSONObject)
        : undefined;
    }

    return typeof value === 'string' ? parseObjectValue(value) : undefined;
  }

  // Primitive field. Coerce non-string values to string before serializing:
  // form values seeded from existing options preserve their JSON type (e.g. a
  // numeric `port`), and serializePrimitiveValue expects a string to parse.
  // Without this, numeric/boolean fields are silently dropped from the payload.
  if (typeof value === 'string') {
    return serializePrimitiveValue(fieldSchema.type, value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return serializePrimitiveValue(fieldSchema.type, String(value));
  }
  return undefined;
}

export function hasSchemaFieldValue(
  fieldSchema: SchemaProperty,
  value: SchemaFieldValue | undefined
): boolean {
  const serializedValue = serializeSchemaFieldValue(fieldSchema, value);

  if (serializedValue === undefined) {
    return false;
  }

  if (
    fieldSchema.type === 'object' &&
    serializedValue != null &&
    typeof serializedValue === 'object' &&
    !Array.isArray(serializedValue)
  ) {
    const requiredSubFields = fieldSchema.required ?? [];
    const keys = Object.keys(serializedValue);

    if (requiredSubFields.length > 0) {
      return requiredSubFields.every((subKey) => subKey in serializedValue);
    }

    return keys.length > 0;
  }

  return true;
}

export function serializeSchemaValues(
  schema: FormSchema,
  values: Record<string, SchemaFieldValue>
): JSONObject {
  const entries = Object.entries(schema.properties).flatMap(
    ([fieldName, fieldSchema]) => {
      // x-enabled-by: omit fields hidden by an unmet condition so disabled
      // fields never carry stale values into the submitted payload (ADR-010).
      if (!isFieldEnabled(fieldSchema.xEnabledBy, values)) return [];

      const serializedValue = serializeSchemaFieldValue(
        fieldSchema,
        values[fieldName]
      );

      return serializedValue === undefined
        ? []
        : [[fieldName, serializedValue] as const];
    }
  );

  return Object.fromEntries(entries) as JSONObject;
}

/**
 * Returns true when any field in the form has a validation error based on
 * schema constraints (min/max, pattern, etc.). Recurses into nested object
 * sub-fields. Consumers can use this to disable submit buttons alongside
 * the existing required-field checks.
 */
export function hasSchemaValidationErrors(
  schema: FormSchema,
  values: Record<string, SchemaFieldValue>
): boolean {
  return Object.entries(schema.properties).some(
    ([fieldName, fieldSchema]) =>
      // x-enabled-by: a field hidden by an unmet condition is not rendered, so
      // its value must not block submission (ADR-010).
      isFieldEnabled(fieldSchema.xEnabledBy, values) &&
      hasFieldValidationError(fieldSchema, values[fieldName])
  );
}

/** Recursively checks a single field (and its sub-properties) for errors. */
function hasFieldValidationError(
  fieldSchema: SchemaProperty,
  value: SchemaFieldValue | undefined
): boolean {
  if (validateField(fieldSchema, value) != null) return true;

  // Recurse into nested object sub-properties
  if (fieldSchema.type === 'object' && fieldSchema.properties) {
    const objectValue =
      value != null && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {};
    const subRequired = fieldSchema.required ?? [];
    return Object.entries(fieldSchema.properties).some(
      ([subKey, subSchema]) => {
        const subValue = normalizeNestedFieldValue(objectValue[subKey]) ?? '';
        // Also check sub-required emptiness
        if (subRequired.includes(subKey) && !subValue) return true;
        return hasFieldValidationError(subSchema, subValue);
      }
    );
  }

  return false;
}

type SchemaFormFieldsProps = {
  schema: FormSchema;
  values: Record<string, SchemaFieldValue>;
  onChange: (field: string, value: SchemaFieldValue) => void;
  disabled?: boolean;
  /** Tenant ID — required for x-lookup catalog queries */
  tenantId?: string;
  /** Connector ID — required for x-lookup catalog queries */
  connectorId?: string;
  getHelperTooltipFooter?: (
    fieldName: string,
    fieldSchema: SchemaProperty
  ) => ReactNode;
};

function isSensitiveField(fieldName: string): boolean {
  const lower = fieldName.toLowerCase();
  return (
    lower.includes('key') ||
    lower.includes('secret') ||
    lower.includes('token') ||
    lower.includes('password')
  );
}

/** Maps JSON Schema `format` to HTML input type where applicable. */
const FORMAT_TO_INPUT_TYPE: Record<string, string> = {
  email: 'email',
  uri: 'url',
  'uri-reference': 'url',
  date: 'date',
  'date-time': 'datetime-local',
  password: 'password',
};

/** Resolves `helperText` from schema description when it differs from the label. */
function resolveHelperText(
  fieldSchema: SchemaProperty,
  label: string
): string | undefined {
  if (fieldSchema.description && fieldSchema.description !== label) {
    return fieldSchema.description;
  }
  return undefined;
}

/** Renders an info icon tooltip for the given helper text. */
function HelperTooltip({
  text,
  footer,
}: Readonly<{ text: string; footer?: ReactNode }>) {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center transition-colors"
        aria-label={text}
      >
        <Info size={getIconPixels('xs')} />
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="max-w-sm p-3">
        <div className="flex flex-col gap-3">
          <div className="text-xs">{text}</div>
          {footer && (
            <div className="border-border/80 border-t pt-3">{footer}</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Builds a placeholder string from examples or a fallback label. */
function resolvePlaceholder(
  fieldSchema: SchemaProperty,
  label: string
): string {
  if (fieldSchema.examples && fieldSchema.examples.length > 0) {
    return `e.g. ${String(fieldSchema.examples[0])}`;
  }
  return `Enter ${label.toLowerCase()}`;
}

/** Computes `min` for a number input from inclusive minimum only.
 * Exclusive bounds are not mapped to HTML min — `validateField` handles them. */
function resolveMin(fieldSchema: SchemaProperty): number | undefined {
  return fieldSchema.minimum ?? undefined;
}

/** Computes `max` for a number input from inclusive maximum only.
 * Exclusive bounds are not mapped to HTML max — `validateField` handles them. */
function resolveMax(fieldSchema: SchemaProperty): number | undefined {
  return fieldSchema.maximum ?? undefined;
}

/**
 * Validates a field value against schema constraints and returns an error
 * message string, or undefined when valid. Runs on every render so the user
 * gets immediate feedback without needing to submit.
 */
function validateField(
  fieldSchema: SchemaProperty,
  value: SchemaFieldValue | undefined
): string | undefined {
  // Skip validation for empty values — `required` is handled separately by
  // the HTML required attribute / consumer-side checks.
  if (value == null || value === '') return undefined;

  const strVal = typeof value === 'string' ? value : '';

  // ── String constraints ──────────────────────────────────────────────
  if (
    typeof value === 'string' &&
    (fieldSchema.type === 'string' || fieldSchema.type === undefined)
  ) {
    if (
      fieldSchema.minLength != null &&
      strVal.length < fieldSchema.minLength
    ) {
      return `Must be at least ${fieldSchema.minLength} characters`;
    }
    if (
      fieldSchema.maxLength != null &&
      strVal.length > fieldSchema.maxLength
    ) {
      return `Must be at most ${fieldSchema.maxLength} characters`;
    }
    if (fieldSchema.pattern) {
      try {
        const re = new RegExp(fieldSchema.pattern);
        if (!re.test(strVal)) {
          return `Must match pattern: ${fieldSchema.pattern}`;
        }
      } catch {
        // Invalid regex in schema — skip validation rather than crash
      }
    }
  }

  // ── Numeric constraints ─────────────────────────────────────────────
  if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
    if (strVal === '') return undefined;
    const num = Number(strVal);
    if (!Number.isFinite(num)) return 'Must be a valid number';
    if (fieldSchema.type === 'integer' && !Number.isInteger(num)) {
      return 'Must be a whole number';
    }
    if (fieldSchema.minimum != null && num < fieldSchema.minimum) {
      return `Must be at least ${fieldSchema.minimum}`;
    }
    if (fieldSchema.maximum != null && num > fieldSchema.maximum) {
      return `Must be at most ${fieldSchema.maximum}`;
    }
    if (
      fieldSchema.exclusiveMinimum != null &&
      num <= fieldSchema.exclusiveMinimum
    ) {
      return `Must be greater than ${fieldSchema.exclusiveMinimum}`;
    }
    if (
      fieldSchema.exclusiveMaximum != null &&
      num >= fieldSchema.exclusiveMaximum
    ) {
      return `Must be less than ${fieldSchema.exclusiveMaximum}`;
    }
  }

  if (fieldSchema.type === 'object' && !fieldSchema.properties) {
    if (typeof value === 'string') {
      return parseObjectValue(value)
        ? undefined
        : 'Must be a valid JSON object';
    }
  }

  return undefined;
}

/**
 * Renders a single schema property as one or more form inputs.
 *
 * - string/default  → FormInput (password type for sensitive names)
 * - textarea format → FormTextarea
 * - object with properties → grouped sub-fields driven by the template
 * - object without properties → KeyValueField with visual and JSON modes
 */
function SchemaField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  required,
  disabled,
  tenantId,
  connectorId,
  helperTooltipFooter,
}: {
  fieldName: string;
  fieldSchema: SchemaProperty;
  value: SchemaFieldValue;
  onChange: (value: SchemaFieldValue) => void;
  required: boolean;
  disabled?: boolean;
  tenantId?: string;
  connectorId?: string;
  helperTooltipFooter?: ReactNode;
}) {
  const fieldId = useId();
  const label = fieldSchema.title ?? fieldSchema.description ?? fieldName;
  const helperText = resolveHelperText(fieldSchema, label);
  const labelSuffix = helperText ? (
    <HelperTooltip text={helperText} footer={helperTooltipFooter} />
  ) : undefined;
  const error = validateField(fieldSchema, value);
  const sensitive =
    fieldSchema.format === 'password' ||
    (!fieldSchema.xLookup && isSensitiveField(fieldName));
  const formatInputType = fieldSchema.format
    ? FORMAT_TO_INPUT_TYPE[fieldSchema.format]
    : undefined;
  const inputType = sensitive ? 'password' : (formatInputType ?? 'text');

  // ── x-lookup: catalog-backed field (ADR-007, ADR-015) ────────────────
  const lookupConfig =
    tenantId && connectorId && fieldSchema.xLookup
      ? { tenantId, connectorId, xLookup: fieldSchema.xLookup }
      : undefined;
  const lookup = useCatalogLookup(lookupConfig);
  const lookupSettled = !!lookupConfig && !lookup.isPending;
  const hasLookupOptions = !!lookupConfig && lookup.options.length > 0;
  const lookupAllowsManualEntry =
    lookupConfig?.xLookup.formInputType === 'datalist';

  // Every source empty or errored — the union produced nothing (ADR-015 §4).
  const allSourcesUnavailable = lookupSettled && !hasLookupOptions;
  const allSourcesErrored =
    !!lookupConfig &&
    lookup.erroredSources.length === lookupConfig.xLookup.sources.length;

  // Disable the fallback control only when every source is a reachable but
  // unpopulated collection (the "run the collector" signal). A pure transport
  // failure keeps manual entry available, as does datalist.
  const disableForEmptyCatalog = allSourcesUnavailable && !allSourcesErrored;
  const effectiveDisabled =
    disabled || (disableForEmptyCatalog && !lookupAllowsManualEntry);

  // A per-source warning surfaces whenever any source could not contribute
  // options, whether or not others resolved (partial failure, ADR-015 §4).
  const showLookupWarning =
    lookupSettled &&
    (lookup.emptySources.length > 0 || lookup.erroredSources.length > 0);
  const effectiveLabelSuffix = showLookupWarning ? (
    <>
      {labelSuffix}
      <LookupSourceWarning
        emptySources={lookup.emptySources}
        erroredSources={lookup.erroredSources}
      />
    </>
  ) : (
    labelSuffix
  );

  if (lookupConfig && hasLookupOptions) {
    const rendered = renderLookupField({
      fieldId,
      formInputType: lookupConfig.xLookup.formInputType,
      options: lookup.options,
      groups: lookup.groups,
      label,
      labelSuffix: effectiveLabelSuffix,
      error,
      value,
      onChange,
      required,
      disabled,
      inputType,
      placeholder: resolvePlaceholder(fieldSchema, label),
    });
    if (rendered) return <>{rendered}</>;
  }

  // ── const: read-only display ──────────────────────────────────────────
  if (fieldSchema.const != null) {
    return (
      <FormInput
        label={label}
        labelSuffix={effectiveLabelSuffix}
        value={String(fieldSchema.const)}
        readOnly
        disabled
      />
    );
  }

  // ── enum: dropdown select ─────────────────────────────────────────────
  if (fieldSchema.enum && fieldSchema.enum.length > 0) {
    return (
      <FormSelect
        label={label}
        labelSuffix={effectiveLabelSuffix}
        error={error}
        selectLabel={`Select ${label.toLowerCase()}`}
        value={(value as string) || ''}
        onValueChange={(v) => onChange(v)}
        required={required}
        disabled={effectiveDisabled}
      >
        {fieldSchema.enum.map((opt) => {
          const optStr = String(opt);
          return (
            <SelectItem key={optStr} value={optStr}>
              {optStr}
            </SelectItem>
          );
        })}
      </FormSelect>
    );
  }

  // ── object ────────────────────────────────────────────────────────────
  if (fieldSchema.type === 'object') {
    const subProperties = fieldSchema.properties;
    const subRequired = fieldSchema.required ?? [];
    const objectValue = isRecordValue(value) ? value : {};

    const handleSubChange = (subKey: string, subVal: string) => {
      onChange({ ...objectValue, [subKey]: subVal });
    };

    if (subProperties && Object.keys(subProperties).length > 0) {
      return (
        <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0 sm:gap-2">
          <legend className="text-foreground float-none w-full text-base font-semibold sm:text-sm">
            {label}
            {required && (
              <span className="text-error ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </legend>
          <div className="border-border flex flex-col gap-1 border-l pl-3">
            {Object.entries(subProperties).map(([subKey, subSchema]) => (
              <SchemaField
                key={subKey}
                fieldName={subKey}
                fieldSchema={subSchema}
                value={normalizeNestedFieldValue(objectValue[subKey]) ?? ''}
                onChange={(subVal) => handleSubChange(subKey, subVal as string)}
                required={subRequired.includes(subKey)}
                disabled={effectiveDisabled}
                tenantId={tenantId}
                connectorId={connectorId}
                helperTooltipFooter={helperTooltipFooter}
              />
            ))}
          </div>
        </fieldset>
      );
    }

    // Object with no defined sub-properties — key-value entry grid
    return (
      <KeyValueField
        label={label}
        value={isRecordValue(value) ? value : (value as string)}
        onChange={onChange}
        required={required}
        disabled={effectiveDisabled}
        labelSuffix={effectiveLabelSuffix}
        error={error}
      />
    );
  }

  // ── textarea format ───────────────────────────────────────────────────
  if (fieldSchema.format === 'textarea') {
    return (
      <FormTextarea
        label={label}
        labelSuffix={effectiveLabelSuffix}
        error={error}
        placeholder={resolvePlaceholder(fieldSchema, label)}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        minLength={fieldSchema.minLength}
        maxLength={fieldSchema.maxLength}
        required={required}
        disabled={effectiveDisabled}
      />
    );
  }

  // ── boolean ───────────────────────────────────────────────────────────
  if (fieldSchema.type === 'boolean') {
    return (
      <FormCheckbox
        label={label}
        labelSuffix={effectiveLabelSuffix}
        checked={(value as string) === 'true'}
        onChange={(e) => onChange(String(e.target.checked))}
        required={required}
        disabled={effectiveDisabled}
      />
    );
  }

  // ── number / integer ──────────────────────────────────────────────────
  if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
    return (
      <FormInput
        label={label}
        labelSuffix={effectiveLabelSuffix}
        error={error}
        placeholder={resolvePlaceholder(fieldSchema, label)}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        min={resolveMin(fieldSchema)}
        max={resolveMax(fieldSchema)}
        step={fieldSchema.type === 'integer' ? 1 : undefined}
        autoComplete="off"
        required={required}
        disabled={effectiveDisabled}
      />
    );
  }

  // ── array ─────────────────────────────────────────────────────────────
  if (fieldSchema.type === 'array') {
    if (disableForEmptyCatalog) {
      return (
        <FormInput
          label={label}
          labelSuffix={effectiveLabelSuffix}
          placeholder={resolvePlaceholder(fieldSchema, label)}
          value=""
          readOnly
          disabled
          required={required}
        />
      );
    }
    return (
      <ArraySchemaField
        label={label}
        labelSuffix={effectiveLabelSuffix}
        value={(value as string) || ''}
        onChange={onChange}
        required={required}
        disabled={effectiveDisabled}
      />
    );
  }

  // ── string (default) ─────────────────────────────────────────────────
  return (
    <FormInput
      label={label}
      labelSuffix={effectiveLabelSuffix}
      error={error}
      placeholder={resolvePlaceholder(fieldSchema, label)}
      value={(value as string) || ''}
      onChange={(e) => onChange(e.target.value)}
      type={inputType}
      showPasswordToggle={sensitive}
      autoComplete="off"
      required={required}
      disabled={effectiveDisabled}
      minLength={fieldSchema.minLength}
      maxLength={fieldSchema.maxLength}
      pattern={fieldSchema.pattern}
      title={
        fieldSchema.pattern
          ? `Must match pattern: ${fieldSchema.pattern}`
          : undefined
      }
    />
  );
}

/**
 * Dynamically renders form fields based on a JSON schema, following the
 * template structure exactly — users fill in the values defined by the template.
 */
export default function SchemaFormFields({
  schema,
  values,
  onChange,
  disabled,
  tenantId,
  connectorId,
  getHelperTooltipFooter,
}: SchemaFormFieldsProps) {
  const { properties, required = [] } = schema;

  return (
    <>
      {Object.entries(properties)
        // x-enabled-by: skip fields whose visibility condition is not met by
        // the current sibling values, so they are not rendered (ADR-010).
        .filter(([, fieldSchema]) =>
          isFieldEnabled(fieldSchema.xEnabledBy, values)
        )
        .map(([fieldName, fieldSchema]) => (
          <SchemaField
            key={fieldName}
            fieldName={fieldName}
            fieldSchema={fieldSchema}
            value={values[fieldName] ?? ''}
            onChange={(val) => onChange(fieldName, val)}
            required={required.includes(fieldName)}
            disabled={disabled}
            tenantId={tenantId}
            connectorId={connectorId}
            helperTooltipFooter={getHelperTooltipFooter?.(
              fieldName,
              fieldSchema
            )}
          />
        ))}
    </>
  );
}
